/**
 * Native Device Integrations Service
 * 
 * Provides native device integration capabilities for:
 * - Phone Calendar (add events, sync)
 * - Phone Dialer (make calls)
 * - Email Composer (send emails)
 * - Maps/Navigation (get directions)
 * 
 * Supports both native Capacitor environments and web fallbacks.
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { toast } from 'sonner';

// Check if running in native environment
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Calendar Event Status
 */
export const CALENDAR_STATUS = {
  NOT_ADDED: 'not_added',
  ADDED: 'added',
  SYNCED: 'synced',
  PERMISSION_NEEDED: 'permission_needed',
};

/**
 * Format phone number for display (human-readable format)
 * Converts raw digits to (XXX) XXX-XXXX for US numbers
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters except leading +
  const hasPlus = phone.startsWith('+');
  let digits = phone.replace(/[^\d]/g, '');
  
  // Handle US numbers (10 or 11 digits)
  if (digits.length === 11 && digits.startsWith('1')) {
    // Remove the leading 1 for formatting
    digits = digits.substring(1);
  }
  
  if (digits.length === 10) {
    // US format: (XXX) XXX-XXXX
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  
  if (digits.length === 7) {
    // Local format: XXX-XXXX
    return `${digits.substring(0, 3)}-${digits.substring(3)}`;
  }
  
  // International or other formats - just return with + if present
  if (hasPlus) {
    return `+${digits}`;
  }
  
  return phone; // Return original if can't format
};

/**
 * Format phone number for dialing (remove formatting, keep + for international)
 * Converts vanity letters (e.g., COOL) to digits
 */
export const formatPhoneForDialing = (phone) => {
  if (!phone) return null;
  
  // Letter to digit mapping for vanity numbers
  const letterToDigit = (char) => {
    const mapping = {
      'A': '2', 'B': '2', 'C': '2',
      'D': '3', 'E': '3', 'F': '3',
      'G': '4', 'H': '4', 'I': '4',
      'J': '5', 'K': '5', 'L': '5',
      'M': '6', 'N': '6', 'O': '6',
      'P': '7', 'Q': '7', 'R': '7', 'S': '7',
      'T': '8', 'U': '8', 'V': '8',
      'W': '9', 'X': '9', 'Y': '9', 'Z': '9',
    };
    return mapping[char.toUpperCase()] || char;
  };
  
  let formatted = phone.trim();
  const hasPlus = formatted.startsWith('+');
  // Convert vanity letters to digits and remove other non-digit characters
  formatted = formatted.split('').map(letterToDigit).join('');
  formatted = formatted.replace(/[^\d]/g, '');
  return hasPlus ? `+${formatted}` : formatted;
};

/**
 * Format phone number with extension for dialing
 * Extensions are typically handled with , (pause) or ; (wait)
 */
export const formatPhoneWithExtension = (phone, extension) => {
  const baseNumber = formatPhoneForDialing(phone);
  if (!baseNumber) return null;
  if (!extension) return baseNumber;
  // Use comma for a pause before dialing extension
  return `${baseNumber},${extension}`;
};

/**
 * Validate phone number (basic validation)
 */
/**
 * Validate phone number
 * Accepts standard phone formats and vanity numbers (letters like 1-800-FLOWERS)
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  // Convert letters to digits for vanity numbers (e.g., COOL -> 2665)
  const letterToDigit = (char) => {
    const mapping = {
      'A': '2', 'B': '2', 'C': '2',
      'D': '3', 'E': '3', 'F': '3',
      'G': '4', 'H': '4', 'I': '4',
      'J': '5', 'K': '5', 'L': '5',
      'M': '6', 'N': '6', 'O': '6',
      'P': '7', 'Q': '7', 'R': '7', 'S': '7',
      'T': '8', 'U': '8', 'V': '8',
      'W': '9', 'X': '9', 'Y': '9', 'Z': '9',
    };
    return mapping[char.toUpperCase()] || char;
  };
  
  // Convert vanity letters to digits
  const converted = phone.split('').map(letterToDigit).join('');
  const digits = converted.replace(/[^\d]/g, '');
  return digits.length >= 7 && digits.length <= 15;
};

/**
 * Validate email address
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Phone Dialer Integration
 * Opens the native phone dialer with the number prefilled
 */
export const openPhoneDialer = async (phone, extension = null) => {
  const formattedPhone = extension 
    ? formatPhoneWithExtension(phone, extension)
    : formatPhoneForDialing(phone);
  
  if (!formattedPhone) {
    toast.error('Invalid phone number');
    return false;
  }

  try {
    const telUrl = `tel:${formattedPhone}`;
    
    if (isNative()) {
      // On native, use the Browser plugin to open tel: URL
      // This triggers the native phone app
      await Browser.open({ url: telUrl, windowName: '_system' });
    } else {
      // Web fallback - use window.location
      window.location.href = telUrl;
    }
    return true;
  } catch (error) {
    console.error('Failed to open phone dialer:', error);
    toast.error("We couldn't open the phone app. Please dial manually.", {
      description: formattedPhone,
      action: {
        label: 'Copy',
        onClick: () => copyToClipboard(formattedPhone),
      },
    });
    return false;
  }
};

/**
 * Email Composer Integration
 * Opens the default email app with a new message
 */
export const openEmailComposer = async (email, subject = '', body = '') => {
  if (!email || !isValidEmail(email)) {
    toast.error('Invalid email address');
    return false;
  }

  try {
    // Build mailto URL with optional subject and body
    let mailtoUrl = `mailto:${encodeURIComponent(email.trim())}`;
    const params = [];
    
    if (subject) {
      params.push(`subject=${encodeURIComponent(subject)}`);
    }
    if (body) {
      params.push(`body=${encodeURIComponent(body)}`);
    }
    
    if (params.length > 0) {
      mailtoUrl += `?${params.join('&')}`;
    }
    
    if (isNative()) {
      await Browser.open({ url: mailtoUrl, windowName: '_system' });
    } else {
      window.location.href = mailtoUrl;
    }
    return true;
  } catch (error) {
    console.error('Failed to open email composer:', error);
    // Offer to copy email address
    toast.error('No email app available. The address has been copied.', {
      action: {
        label: 'Copy',
        onClick: () => copyToClipboard(email),
      },
    });
    await copyToClipboard(email);
    return false;
  }
};

/**
 * Build a full address string from components
 */
export const buildAddressString = (addressData) => {
  if (typeof addressData === 'string') return addressData;
  
  const parts = [];
  
  if (addressData.street) parts.push(addressData.street);
  if (addressData.city) parts.push(addressData.city);
  if (addressData.state) parts.push(addressData.state);
  if (addressData.postalCode || addressData.zip) {
    parts.push(addressData.postalCode || addressData.zip);
  }
  if (addressData.country) parts.push(addressData.country);
  
  return parts.join(', ');
};

/**
 * Maps/Navigation Integration
 * Opens the native maps app with directions to an address
 */
export const openMapsWithDirections = async (address, businessName = '') => {
  const addressString = typeof address === 'string' ? address : buildAddressString(address);
  
  if (!addressString && !businessName) {
    toast.error('Address information is incomplete.');
    return false;
  }

  try {
    // Build search query - include business name for better results
    let searchQuery = addressString;
    if (businessName && addressString) {
      searchQuery = `${businessName}, ${addressString}`;
    } else if (businessName) {
      searchQuery = businessName;
    }
    
    const encodedQuery = encodeURIComponent(searchQuery);
    let mapsUrl;
    
    const platform = getPlatform();
    
    if (platform === 'ios') {
      // Apple Maps URL scheme
      mapsUrl = `maps://maps.apple.com/?q=${encodedQuery}`;
    } else if (platform === 'android') {
      // Google Maps intent URL for Android
      mapsUrl = `geo:0,0?q=${encodedQuery}`;
    } else {
      // Web fallback - Google Maps
      mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
    }
    
    if (isNative()) {
      await Browser.open({ url: mapsUrl, windowName: '_system' });
    } else {
      // Web fallback - open in new tab
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedQuery}`, '_blank');
    }
    
    return true;
  } catch (error) {
    console.error('Failed to open maps:', error);
    const fallbackMessage = addressString 
      ? "This address is incomplete, but we'll search using the information available."
      : "We couldn't open the maps app.";
    
    toast.error(fallbackMessage, {
      action: {
        label: 'Copy Address',
        onClick: () => copyToClipboard(addressString || businessName),
      },
    });
    
    // Try web fallback
    try {
      const encodedQuery = encodeURIComponent(addressString || businessName);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedQuery}`, '_blank');
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  if (!text) return false;
  
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    toast.success('Copied to clipboard');
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    toast.error('Failed to copy');
    return false;
  }
};

/**
 * Calendar Integration Service
 * Note: Full calendar integration requires the @capacitor-community/calendar plugin
 * For now, we provide web fallbacks and prepare for native integration
 */
class CalendarService {
  constructor() {
    this.hasPermission = false;
    this.isAvailable = false;
  }

  /**
   * Check if calendar integration is available
   */
  async checkAvailability() {
    // Calendar plugin would be checked here
    // For now, return true for web (uses fallback)
    this.isAvailable = true;
    return this.isAvailable;
  }

  /**
   * Request calendar permission
   */
  async requestPermission() {
    if (!isNative()) {
      // Web doesn't need permission - uses download/add link
      this.hasPermission = true;
      return true;
    }

    // Native calendar permission would be requested here
    // This requires @capacitor-community/calendar plugin
    try {
      // Placeholder for native implementation
      this.hasPermission = true;
      return true;
    } catch (error) {
      console.error('Calendar permission denied:', error);
      toast.error('Calendar access is turned off. You can enable it in Settings.', {
        duration: 5000,
      });
      return false;
    }
  }

  /**
   * Generate ICS file content for calendar event
   */
  generateICSContent(event) {
    const {
      title,
      description = '',
      location = '',
      startDate,
      endDate,
      allDay = false,
      recurrence = null, // 'yearly', 'monthly', 'weekly', 'daily'
      reminder = null, // minutes before
    } = event;

    const formatDate = (date, allDay) => {
      const d = new Date(date);
      if (allDay) {
        return d.toISOString().split('T')[0].replace(/-/g, '');
      }
      return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const uid = `fmn-${Date.now()}-${Math.random().toString(36).substring(2, 11)}@forgetmenot.app`;
    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    let ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Forget Me Not//Life Admin//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
    ];

    if (allDay) {
      ics.push(`DTSTART;VALUE=DATE:${formatDate(startDate, true)}`);
      if (endDate) {
        ics.push(`DTEND;VALUE=DATE:${formatDate(endDate, true)}`);
      }
    } else {
      ics.push(`DTSTART:${formatDate(startDate, false)}`);
      if (endDate) {
        ics.push(`DTEND:${formatDate(endDate, false)}`);
      }
    }

    ics.push(`SUMMARY:${this.escapeICS(title)}`);
    
    if (description) {
      ics.push(`DESCRIPTION:${this.escapeICS(description)}`);
    }
    
    if (location) {
      ics.push(`LOCATION:${this.escapeICS(location)}`);
    }

    // Recurrence rule
    if (recurrence) {
      const rruleMap = {
        yearly: 'RRULE:FREQ=YEARLY',
        monthly: 'RRULE:FREQ=MONTHLY',
        weekly: 'RRULE:FREQ=WEEKLY',
        daily: 'RRULE:FREQ=DAILY',
      };
      if (rruleMap[recurrence]) {
        ics.push(rruleMap[recurrence]);
      }
    }

    // Reminder alarm
    if (reminder && reminder > 0) {
      ics.push('BEGIN:VALARM');
      ics.push('ACTION:DISPLAY');
      ics.push(`DESCRIPTION:Reminder: ${title}`);
      ics.push(`TRIGGER:-PT${reminder}M`);
      ics.push('END:VALARM');
    }

    ics.push('END:VEVENT');
    ics.push('END:VCALENDAR');

    return ics.join('\r\n');
  }

  /**
   * Escape special characters for ICS format
   */
  escapeICS(text) {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  }

  /**
   * Add event to calendar via ICS export (FALLBACK / MANUAL OPTION ONLY)
   * 
   * NOTE: This is NOT automatic native sync. It downloads an ICS file that
   * the user must manually import. For true native sync, use nativeCalendarService.js
   * 
   * Use cases:
   * - Web browser fallback (no native calendar access)
   * - Manual "Export to Calendar" option
   * - "Add Once" one-time events
   */
  async addEvent(eventData) {
    const {
      title,
      description,
      location,
      startDate,
      endDate,
      allDay,
      recurrence,
      reminder,
    } = eventData;

    if (!title || !startDate) {
      toast.error('Event title and date are required');
      return { success: false };
    }

    try {
      if (isNative()) {
        // Native: Generate ICS and open with system (manual import)
        const icsContent = this.generateICSContent(eventData);
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        
        // Open ICS file which triggers native calendar picker
        await Browser.open({ url, windowName: '_system' });
        
        toast.success('Opening calendar app...', {
          description: 'Review and save the event in your calendar.',
        });
        
        // Note: This does NOT return a native event ID - it's manual import
        return { success: true, status: CALENDAR_STATUS.ADDED, isManualImport: true };
      } else {
        // Web: Download ICS file (user must manually import)
        const icsContent = this.generateICSContent(eventData);
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('Calendar file downloaded', {
          description: 'Open the .ics file to add to your calendar.',
        });
        
        return { success: true, status: CALENDAR_STATUS.ADDED };
      }
    } catch (error) {
      console.error('Failed to add calendar event:', error);
      toast.error('Failed to add event to calendar');
      return { success: false, error };
    }
  }

  /**
   * Create calendar event from a Forget Me Not date record
   */
  createEventFromDate(dateRecord) {
    const {
      title,
      name,
      description,
      notes,
      date,
      time,
      end_time,
      location,
      all_day,
      reminder_minutes,
      recurrence,
      type,
    } = dateRecord;

    // Determine if this is a recurring annual event
    const isAnnualEvent = ['birthday', 'anniversary'].includes(type) || recurrence === 'yearly';

    // Build start date with time if available
    let startDate = new Date(date);
    if (time && !all_day) {
      const [hours, minutes] = time.split(':');
      startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    }

    // Build end date
    let endDate = null;
    if (end_time && !all_day) {
      endDate = new Date(date);
      const [hours, minutes] = end_time.split(':');
      endDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    }

    return {
      title: title || name || 'Forget Me Not Event',
      description: description || notes || '',
      location: location || '',
      startDate,
      endDate,
      allDay: all_day !== false,
      recurrence: isAnnualEvent ? 'yearly' : (recurrence || null),
      reminder: reminder_minutes || 60, // Default 1 hour reminder
    };
  }
}

// Export singleton instance
export const calendarService = new CalendarService();

export default {
  isNative,
  getPlatform,
  openPhoneDialer,
  openEmailComposer,
  openMapsWithDirections,
  copyToClipboard,
  calendarService,
  formatPhoneForDialing,
  isValidPhone,
  isValidEmail,
  buildAddressString,
  CALENDAR_STATUS,
};
